// Lógica pura de la Agenda de hitos (sin React ni DOM, testeable).
// Cada evento es fecha + tipo + obra; los hitos críticos (hitosModel.js) llevan además
// su situación: confirmado, no completado, en riesgo...

import { dateFromIso, portfolioToday, toIsoDate } from "./portfolioShared";
import { hitoState, isCriticalHito } from "../hitosModel";

// Conjunto estándar de hitos con fecha de una obra, en orden natural de ejecución.
// El prefijo identifica el tipo por texto (no solo por color) en cada evento.
export const MILESTONE_TYPES = [
    { id: "inicioObra", prefix: "INI", label: "Inicio de obra" },
    { id: "zonif", prefix: "ZON", label: "Zonificación" },
    { id: "legal", prefix: "LEG", label: "Legal" },
    { id: "planoP3", prefix: "P3", label: "Plano P3" },
    { id: "p3Constructora", prefix: "P3C", label: "P3 constructora" },
    { id: "p3Mobiliario", prefix: "P3M", label: "P3 mobiliario" },
    { id: "deadlineMob", prefix: "MOB", label: "Deadline MOB" },
    { id: "fechaIluminacion", prefix: "ILU", label: "Iluminación" },
    { id: "fechaSuelo", prefix: "SUE", label: "Suelo" },
    { id: "inicioMontajeMob", prefix: "MNI", label: "Inicio montaje mobiliario" },
    { id: "finMontajeMob", prefix: "MNF", label: "Fin montaje mobiliario" },
    { id: "apertura", prefix: "APE", label: "Apertura" },
];
// Tipos que existían antes de guardar la selección como { selected, known } (una
// selección antigua es solo la lista de marcados de estos ocho).
const LEGACY_TYPE_IDS = ["inicioObra", "zonif", "legal", "planoP3", "deadlineMob", "fechaIluminacion", "fechaSuelo", "apertura"];

export const MILESTONE_TYPE_IDS = MILESTONE_TYPES.map((type) => type.id);
const TYPE_ORDER = MILESTONE_TYPE_IDS.reduce((acc, id, index) => ({ ...acc, [id]: index }), {});

export const AGENDA_TYPES_STORAGE_KEY = "obras.agenda.milestoneTypes.v1";
// Vista Semana retirada (sep 2026): un modo guardado "semana" vuelve a "mes".
export const AGENDA_MODES = [
    { id: "mes", label: "Mes" },
    { id: "lista", label: "Lista" },
];

// Fecha ISO yyyy-mm-dd que existe en el calendario (descarta 2026-02-30, vacíos, horas...).
export function isValidIsoDate(value) {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    return toIsoDate(dateFromIso(value)) === value;
}

// Un evento por cada hito con fecha válida. Orden: fecha, tipo (orden estándar), tienda.
// Los hitos críticos llevan `hito` (situación a `today`); el resto, null.
export function buildAgendaEvents(obras = [], today = portfolioToday) {
    const events = [];
    obras.forEach((obra) => {
        MILESTONE_TYPES.forEach((type) => {
            const date = obra?.[type.id];
            if (!isValidIsoDate(date)) return;
            const hito = isCriticalHito(type.id) ? hitoState(obra, type.id, today) : null;
            events.push({ date, type, obra, hito });
        });
    });
    return events.sort((a, b) => a.date.localeCompare(b.date)
        || TYPE_ORDER[a.type.id] - TYPE_ORDER[b.type.id]
        || String(a.obra.codigoTienda ?? "").localeCompare(String(b.obra.codigoTienda ?? ""), "es", { numeric: true }));
}

// Recuento por tipo sobre todos los eventos (no depende de la selección de tipos).
export function countEventsByType(events = []) {
    const counts = MILESTONE_TYPE_IDS.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});
    events.forEach((event) => { counts[event.type.id] += 1; });
    return counts;
}

export function filterEventsByTypes(events = [], selectedIds = []) {
    const selected = new Set(selectedIds);
    return events.filter((event) => selected.has(event.type.id));
}

export function groupEventsByDate(events = []) {
    return events.reduce((acc, event) => {
        (acc[event.date] ||= []).push(event);
        return acc;
    }, {});
}

// Selección -> ids conocidos, sin duplicados y en orden estándar. Sin valor (o
// corrupto) se muestran todos los tipos. Con el formato guardado { selected, known },
// un tipo que no existía al guardar aparece marcado: quien desmarcó algo lo sigue
// viendo desmarcado, pero lo nuevo no se esconde por no haberlo visto nunca.
export function normalizeSelectedTypes(stored) {
    if (Array.isArray(stored)) {
        const selected = new Set(stored);
        return MILESTONE_TYPE_IDS.filter((id) => selected.has(id));
    }
    if (!stored || !Array.isArray(stored.selected)) return [...MILESTONE_TYPE_IDS];
    const selected = new Set(stored.selected);
    const known = new Set(Array.isArray(stored.known) ? stored.known : MILESTONE_TYPE_IDS);
    return MILESTONE_TYPE_IDS.filter((id) => selected.has(id) || !known.has(id));
}

export function toggleSelectedType(selectedIds, typeId) {
    const next = selectedIds.includes(typeId) ? selectedIds.filter((id) => id !== typeId) : [...selectedIds, typeId];
    return normalizeSelectedTypes(next);
}

// localStorage es por navegador/perfil (igual que el resto de preferencias de Obras).
export function loadSelectedTypes(storage = typeof window !== "undefined" ? window.localStorage : null) {
    try {
        const raw = storage?.getItem(AGENDA_TYPES_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        // Formato antiguo: solo la lista de marcados de los ocho tipos de entonces.
        return normalizeSelectedTypes(Array.isArray(parsed) ? { selected: parsed, known: LEGACY_TYPE_IDS } : parsed);
    } catch {
        return [...MILESTONE_TYPE_IDS];
    }
}

export function saveSelectedTypes(selectedIds, storage = typeof window !== "undefined" ? window.localStorage : null) {
    try {
        storage?.setItem(AGENDA_TYPES_STORAGE_KEY, JSON.stringify({ selected: normalizeSelectedTypes(selectedIds), known: MILESTONE_TYPE_IDS }));
    } catch { /* cuota llena o almacenamiento bloqueado: la selección vive solo en memoria */ }
}

// ── Fechas ───────────────────────────────────────────────────────────────────
const atNoon = (year, month, day) => new Date(year, month, day, 12);

const longMonthYearFormatter = new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" });

export function monthKeyOf(date) {
    return `${date.getFullYear()}-${date.getMonth()}`;
}

export function monthLabelOf(year, month) {
    const label = longMonthYearFormatter.format(atNoon(year, month, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
}

// Rango continuo de meses que cubre la primera y la última fecha y el mes de hoy,
// ampliado `before` meses por delante y `after` por detrás (scroll infinito).
// `sortedDates` debe venir ordenado (buildAgendaEvents ya lo ordena).
export function buildMonthRange(sortedDates = [], today, { before = 0, after = 0 } = {}) {
    const candidates = [today, ...(sortedDates.length ? [dateFromIso(sortedDates[0]), dateFromIso(sortedDates[sortedDates.length - 1])] : [])];
    const minDate = candidates.reduce((min, date) => (date < min ? date : min));
    const maxDate = candidates.reduce((max, date) => (date > max ? date : max));
    const first = atNoon(minDate.getFullYear(), minDate.getMonth() - Math.max(0, before), 1);
    const lastMonthStart = atNoon(maxDate.getFullYear(), maxDate.getMonth() + Math.max(0, after), 1);
    const months = [];
    for (let cursor = atNoon(first.getFullYear(), first.getMonth(), 1); cursor <= lastMonthStart; cursor = atNoon(cursor.getFullYear(), cursor.getMonth() + 1, 1)) {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();
        const dayCount = new Date(year, month + 1, 0).getDate();
        months.push({
            key: monthKeyOf(cursor),
            year,
            month,
            label: monthLabelOf(year, month),
            offset: (cursor.getDay() + 6) % 7,
            days: Array.from({ length: dayCount }, (_, index) => {
                const date = atNoon(year, month, index + 1);
                return { date, iso: toIsoDate(date) };
            }),
        });
    }
    return months;
}
